import { Widget, CanvasState } from '../types/index.js';

/**
 * Traces all widgets that contribute data to a target widget
 * @param targetWidgetId The widget to trace backwards from
 * @param canvas The canvas containing widgets and connections
 * @returns Array of widget IDs that form the pipeline
 */
export function tracePipelineBackwards(
  targetWidgetId: string,
  canvas: CanvasState
): string[] {
  const pipeline = new Set<string>([targetWidgetId]);
  const toProcess = [targetWidgetId];
  
  while (toProcess.length > 0) {
    const currentId = toProcess.pop()!;
    
    // Find all connections where current widget is the target
    const incomingConnections = canvas.connections.filter(
      conn => conn.target.widgetId === currentId
    );
    
    // Add source widgets to pipeline and process them
    for (const conn of incomingConnections) {
      if (!pipeline.has(conn.source.widgetId)) {
        pipeline.add(conn.source.widgetId);
        toProcess.push(conn.source.widgetId);
      }
    }
  }
  
  return Array.from(pipeline);
}

/**
 * Determines the primary output widget in a pipeline
 * (The widget with outputs but no outgoing connections within the pipeline)
 */
export function findPipelineOutputWidget(
  widgetIds: string[],
  canvas: CanvasState
): Widget | null {
  const widgetIdSet = new Set(widgetIds);
  
  // Find widgets that have outputs but no connections to other widgets in the pipeline
  const outputCandidates = widgetIds.filter(widgetId => {
    const widget = canvas.widgets.find(w => w.id === widgetId);
    if (!widget || widget.outputs.length === 0) return false;
    
    // Check if this widget has connections to widgets outside the pipeline
    const outgoingConnections = canvas.connections.filter(
      conn => conn.source.widgetId === widgetId && widgetIdSet.has(conn.target.widgetId)
    );
    
    return outgoingConnections.length === 0;
  });
  
  // Return the last widget in the flow (typically has the highest processing order)
  if (outputCandidates.length > 0) {
    const outputWidget = canvas.widgets.find(w => w.id === outputCandidates[outputCandidates.length - 1]);
    return outputWidget || null;
  }
  
  return null;
}

/**
 * Gets all external inputs to a pipeline (inputs from widgets outside the pipeline)
 */
export function getPipelineExternalInputs(
  widgetIds: string[],
  canvas: CanvasState
): Array<{ widgetId: string; inputId: string; sourceWidgetId: string }> {
  const widgetIdSet = new Set(widgetIds);
  const externalInputs: Array<{ widgetId: string; inputId: string; sourceWidgetId: string }> = [];
  
  for (const widgetId of widgetIds) {
    const incomingConnections = canvas.connections.filter(
      conn => conn.target.widgetId === widgetId && !widgetIdSet.has(conn.source.widgetId)
    );
    
    for (const conn of incomingConnections) {
      externalInputs.push({
        widgetId: conn.target.widgetId,
        inputId: conn.target.inputId,
        sourceWidgetId: conn.source.widgetId,
      });
    }
  }
  
  return externalInputs;
}